import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import {
  DEFAULT_ANNOUNCEMENTS,
  MAX_ANNOUNCEMENT_LENGTH,
  MAX_ANNOUNCEMENTS,
} from "./config/announcements";
import { SLIDE_DEFINITIONS } from "./config/slides";
import { FooterBar } from "./components/FooterBar";
import { SettingsDialog } from "./components/SettingsDialog";
import { Slideshow, type RenderedSlide } from "./components/Slideshow";
import {
  CountdownCueSequencer,
  CountdownSoundPlayer,
  createCountdownState,
  formatJstDateTimeInput,
  getCountdownView,
  normalizeSavedStartAtJst,
  parseJstDateTimeInput,
  transitionCountdown,
} from "./features/countdown";
import { useParticipation } from "./hooks/useParticipation";
import { useParticipationRefresh } from "./hooks/useParticipationRefresh";
import { useSynchronizedClock } from "./hooks/useSynchronizedClock";
import { CountdownSlide } from "./slides/CountdownSlide";
import {
  KAKUZUKE_IMAGE_SOURCE,
  LOVE_ADVICE_IMAGE_SOURCE,
  TWENTY_DOORS_IMAGE_SOURCE,
  KakuzukeSlide,
  LoveAdviceSlide,
  TwentyDoorsSlide,
} from "./slides/AdditionalFeatureSlides";
import { OGIRI_IMAGE_SOURCE, OgiriSlide } from "./slides/OgiriSlide";
import { ParticipationSlide } from "./slides/ParticipationSlide";
import { ProgramSlide } from "./slides/ProgramSlide";
import {
  FEATURE_INTRO_SLIDE_IDS,
  SLIDE_IDS,
  type DisplaySettings,
  type SlideId,
} from "./types";
import { MAX_PROJECTION_REFRESH_DURATION_MS } from "../shared/participation-refresh";
import {
  PARTICIPATION_STALE_AFTER_MS,
  type ParticipationApiId,
} from "../shared/participation";

const SETTINGS_STORAGE_KEY = "zenyasai-display-settings-v3";
const LEGACY_V2_SETTINGS_STORAGE_KEY = "zenyasai-display-settings-v2";
const LEGACY_V1_SETTINGS_STORAGE_KEY = "zenyasai-display-settings-v1";
const LEGACY_V2_SLIDE_IDS = ["program", "ogiri", "participation", "countdown"] as const;
const LEGACY_V1_SLIDE_IDS = ["program", "participation", "countdown"] as const;
const PARTICIPATION_SOURCE_REFRESH_INTERVAL_MS = 60_000;
type LegacyV2SlideId = (typeof LEGACY_V2_SLIDE_IDS)[number];
type LegacyV1SlideId = (typeof LEGACY_V1_SLIDE_IDS)[number];

function createDefaultSettings(nowEpochMs: number): DisplaySettings {
  const target = Math.ceil((nowEpochMs + 10 * 60_000) / 60_000) * 60_000;
  return {
    version: 3,
    startAtJst: formatJstDateTimeInput(target),
    apiId: "03",
    enabledSlides: [...SLIDE_IDS],
    announcements: [...DEFAULT_ANNOUNCEMENTS],
    countdownSoundEnabled: false,
    requestFullscreen: true,
  };
}

function isLegacyV2SlideId(value: unknown): value is LegacyV2SlideId {
  return typeof value === "string"
    && (LEGACY_V2_SLIDE_IDS as readonly string[]).includes(value);
}

function isLegacyV1SlideId(value: unknown): value is LegacyV1SlideId {
  return typeof value === "string"
    && (LEGACY_V1_SLIDE_IDS as readonly string[]).includes(value);
}

function insertAfterProgram(
  slides: readonly SlideId[],
  additions: readonly SlideId[],
): SlideId[] {
  const migrated = [...slides];
  const programIndex = migrated.indexOf("program");
  migrated.splice(programIndex >= 0 ? programIndex + 1 : 0, 0, ...additions);
  return migrated;
}

function migrateV1Slides(slides: readonly LegacyV1SlideId[]): SlideId[] {
  return insertAfterProgram(slides, FEATURE_INTRO_SLIDE_IDS);
}

function migrateV2Slides(slides: readonly LegacyV2SlideId[]): SlideId[] {
  const ogiriIndex = slides.indexOf("ogiri");
  if (ogiriIndex < 0) {
    return insertAfterProgram(slides, ["kakuzuke", "twenty-doors", "love-advice"]);
  }

  const migrated: SlideId[] = [...slides];
  migrated.splice(ogiriIndex, 1, ...FEATURE_INTRO_SLIDE_IDS);
  return migrated;
}

function isSlideId(value: unknown): value is SlideId {
  return typeof value === "string" && (SLIDE_IDS as readonly string[]).includes(value);
}

function readAnnouncements(value: unknown): readonly string[] | null {
  if (!Array.isArray(value) || value.length > MAX_ANNOUNCEMENTS) return null;
  const announcements: string[] = [];
  for (const candidate of value) {
    if (typeof candidate !== "string") return null;
    const normalized = candidate.trim();
    if (
      normalized.length === 0 ||
      normalized.length > MAX_ANNOUNCEMENT_LENGTH
    ) return null;
    announcements.push(normalized);
  }
  return announcements;
}

function readInitialSettings(
  nowEpochMs: number,
  onValidStoredStartAtJst?: (value: string) => void,
): DisplaySettings {
  const fallback = createDefaultSettings(nowEpochMs);

  try {
    const currentRaw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    const legacyV2Raw = currentRaw === null
      ? window.localStorage.getItem(LEGACY_V2_SETTINGS_STORAGE_KEY)
      : null;
    const legacyV1Raw = currentRaw === null && legacyV2Raw === null
      ? window.localStorage.getItem(LEGACY_V1_SETTINGS_STORAGE_KEY)
      : null;
    const raw = currentRaw ?? legacyV2Raw ?? legacyV1Raw;
    if (!raw) return fallback;
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return fallback;
    const record = value as Record<string, unknown>;
    const sourceVersion = currentRaw !== null
      ? 3
      : legacyV2Raw !== null
        ? 2
        : 1;
    if (
      record.version !== sourceVersion ||
      typeof record.startAtJst !== "string" ||
      (record.apiId !== "03" && record.apiId !== "04") ||
      !Array.isArray(record.enabledSlides) ||
      (record.countdownSoundEnabled !== undefined &&
        typeof record.countdownSoundEnabled !== "boolean") ||
      typeof record.requestFullscreen !== "boolean"
    ) {
      return fallback;
    }
    const startAtJst = normalizeSavedStartAtJst(
      record.startAtJst,
      nowEpochMs,
    );

    const storedSlides = record.enabledSlides;
    const slidesAreValid = sourceVersion === 3
      ? storedSlides.every(isSlideId)
      : sourceVersion === 2
        ? storedSlides.every(isLegacyV2SlideId)
        : storedSlides.every(isLegacyV1SlideId);
    if (
      storedSlides.length === 0 ||
      !slidesAreValid ||
      new Set(storedSlides).size !== storedSlides.length
    ) return fallback;
    const enabledSlides = sourceVersion === 3
      ? storedSlides as SlideId[]
      : sourceVersion === 2
        ? migrateV2Slides(storedSlides as LegacyV2SlideId[])
        : migrateV1Slides(storedSlides as LegacyV1SlideId[]);
    const announcements = record.announcements === undefined
      ? [...DEFAULT_ANNOUNCEMENTS]
      : readAnnouncements(record.announcements);
    if (announcements === null) return fallback;
    onValidStoredStartAtJst?.(record.startAtJst);

    return {
      version: 3,
      startAtJst,
      apiId: record.apiId,
      enabledSlides,
      announcements,
      countdownSoundEnabled: record.countdownSoundEnabled === true,
      requestFullscreen: record.requestFullscreen,
    };
  } catch {
    return fallback;
  }
}

export default function App() {
  const clock = useSynchronizedClock();
  const persistedStartAtJstRef = useRef<string | null>(null);
  const [settings, setSettings] = useState<DisplaySettings>(() =>
    readInitialSettings(clock.nowEpochMs, (value) => {
      persistedStartAtJstRef.current = value;
    }),
  );
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [projectionRevision, setProjectionRevision] = useState(0);
  const [activeSlideId, setActiveSlideId] = useState<SlideId>(
    settings.enabledSlides[0] ?? "program",
  );
  const [settingsApiId, setSettingsApiId] = useState(settings.apiId);
  const [settingsParticipationEnabled, setSettingsParticipationEnabled] = useState(
    settings.enabledSlides.includes("participation"),
  );
  const [storageError, setStorageError] = useState<string | null>(null);
  const fullscreenAttemptRef = useRef<Promise<void> | null>(null);
  const correctStartAfterClockSyncRef = useRef(!clock.status.synchronized);
  const countdownSoundPlayerRef = useRef(new CountdownSoundPlayer());
  const countdownCueSequencerRef = useRef(new CountdownCueSequencer());

  const [countdownState, dispatchCountdown] = useReducer(
    transitionCountdown,
    undefined,
    () => createCountdownState({ enabled: false }, clock.nowEpochMs),
  );

  const countdownEnabled = settings.enabledSlides.includes("countdown");
  const participationEnabled = settings.enabledSlides.includes("participation");
  const participationApiId = settingsOpen ? settingsApiId : settings.apiId;
  const shouldLoadParticipation = settingsOpen
    ? settingsParticipationEnabled
    : participationEnabled && hasStarted;
  const participation = useParticipation(
    participationApiId,
    shouldLoadParticipation,
  );
  const participationRefresh = useParticipationRefresh();
  const synchronizedNowEpochMsRef = useRef(clock.nowEpochMs);
  synchronizedNowEpochMsRef.current = clock.nowEpochMs;
  const requestParticipationSourceRefresh = useCallback(async (
    apiId: ParticipationApiId,
    targetEpochMs: number,
  ): Promise<boolean> => {
    const remainingMs = targetEpochMs - synchronizedNowEpochMsRef.current;
    if (
      !Number.isSafeInteger(targetEpochMs)
      || remainingMs <= 0
      || remainingMs > MAX_PROJECTION_REFRESH_DURATION_MS
    ) {
      return false;
    }

    const accepted = await participationRefresh.refresh(apiId, targetEpochMs);
    if (accepted) participation.refreshNow();
    return accepted;
  }, [participation.refreshNow, participationRefresh.refresh]);
  const participationPredecessorId = useMemo<SlideId | null>(() => {
    const participationIndex = settings.enabledSlides.indexOf("participation");
    if (participationIndex < 0 || settings.enabledSlides.length <= 1) return null;
    const predecessorIndex = (
      participationIndex - 1 + settings.enabledSlides.length
    ) % settings.enabledSlides.length;
    return settings.enabledSlides[predecessorIndex] ?? null;
  }, [settings.enabledSlides]);
  const projectionTargetEpochMs = useMemo(() => {
    try {
      return parseJstDateTimeInput(settings.startAtJst);
    } catch {
      return null;
    }
  }, [settings.startAtJst]);
  const participationRefreshWindowOpen = hasStarted
    && countdownEnabled
    && participationEnabled
    && projectionTargetEpochMs !== null
    && clock.nowEpochMs < projectionTargetEpochMs;
  const countdownView = getCountdownView(countdownState);
  const participationRefreshedAtMs = participation.data === null
    ? Number.NaN
    : Math.min(Date.parse(participation.data.refreshedAt),
        Date.parse(participation.data.sourceGeneratedAt ?? participation.data.refreshedAt));
  const participationSnapshotAgeMs = clock.nowEpochMs - participationRefreshedAtMs;
  const participationSnapshotIsFresh = participation.data !== null
    && participation.error === null
    && !participation.data.stale
    && Number.isFinite(participationRefreshedAtMs)
    && participationSnapshotAgeMs >= -30_000
    && participationSnapshotAgeMs <= PARTICIPATION_STALE_AFTER_MS;

  useEffect(() => {
    for (const source of [
      KAKUZUKE_IMAGE_SOURCE,
      TWENTY_DOORS_IMAGE_SOURCE,
      OGIRI_IMAGE_SOURCE,
      LOVE_ADVICE_IMAGE_SOURCE,
    ]) {
      const image = new Image();
      image.decoding = "async";
      image.src = source;
      if (typeof image.decode === "function") {
        void image.decode().catch(() => undefined);
      }
    }
  }, []);

  useEffect(() => {
    if (
      !correctStartAfterClockSyncRef.current ||
      !clock.status.synchronized ||
      hasStarted
    ) return;

    correctStartAfterClockSyncRef.current = false;
    setSettings((current) => {
      const normalizedStartAtJst = persistedStartAtJstRef.current === null
        ? createDefaultSettings(clock.nowEpochMs).startAtJst
        : normalizeSavedStartAtJst(
            persistedStartAtJstRef.current,
            clock.nowEpochMs,
          );
      return normalizedStartAtJst === current.startAtJst
        ? current
        : { ...current, startAtJst: normalizedStartAtJst };
    });
  }, [clock.nowEpochMs, clock.status.synchronized, hasStarted]);

  useEffect(() => () => countdownSoundPlayerRef.current.dispose(), []);

  useEffect(() => {
    if (!settingsOpen) return;
    setSettingsApiId(settings.apiId);
    setSettingsParticipationEnabled(
      settings.enabledSlides.includes("participation"),
    );
  }, [settings.apiId, settings.enabledSlides, settingsOpen]);

  useEffect(() => {
    if (
      !hasStarted
      || !countdownEnabled
      || settingsOpen
      || !participationEnabled
      || participationPredecessorId === null
      || activeSlideId !== participationPredecessorId
    ) return;

    if (projectionTargetEpochMs !== null) {
      void requestParticipationSourceRefresh(
        settings.apiId,
        projectionTargetEpochMs,
      );
    }
  }, [
    activeSlideId,
    countdownEnabled,
    hasStarted,
    participationEnabled,
    participationPredecessorId,
    projectionTargetEpochMs,
    requestParticipationSourceRefresh,
    settings.apiId,
    settingsOpen,
  ]);

  useEffect(() => {
    if (!participationRefreshWindowOpen || projectionTargetEpochMs === null) return;

    const intervalId = window.setInterval(() => {
      void requestParticipationSourceRefresh(
        settings.apiId,
        projectionTargetEpochMs,
      );
    }, PARTICIPATION_SOURCE_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [
    participationRefreshWindowOpen,
    projectionTargetEpochMs,
    requestParticipationSourceRefresh,
    settings.apiId,
  ]);

  const openSettings = useCallback((): void => {
    setSettingsApiId(settings.apiId);
    setSettingsParticipationEnabled(
      settings.enabledSlides.includes("participation"),
    );
    setSettingsOpen(true);
  }, [settings.apiId, settings.enabledSlides]);

  const cancelSettings = useCallback((): void => {
    setSettingsApiId(settings.apiId);
    setSettingsParticipationEnabled(
      settings.enabledSlides.includes("participation"),
    );
    setSettingsOpen(false);
  }, [settings.apiId, settings.enabledSlides]);

  const attemptFullscreen = useCallback((): void => {
    if (document.fullscreenElement) return;
    if (fullscreenAttemptRef.current !== null) return;

    const element = document.documentElement;
    const request = Reflect.get(element, "requestFullscreen");
    if (typeof request !== "function") return;

    let result: unknown;
    try {
      result = Reflect.apply(request, element, []);
    } catch {
      return;
    }

    let pending: Promise<void>;
    pending = Promise.resolve(result).then(
      () => undefined,
      () => undefined,
    ).finally(() => {
      if (fullscreenAttemptRef.current === pending) {
        fullscreenAttemptRef.current = null;
      }
    });
    fullscreenAttemptRef.current = pending;
  }, []);

  useEffect(() => {
    if (!hasStarted) return;
    const targetEpochMs = parseJstDateTimeInput(settings.startAtJst);
    dispatchCountdown({
      type: "configure",
      configuration: countdownEnabled
        ? { enabled: true, targetEpochMs }
        : { enabled: false, targetEpochMs },
      nowEpochMs: clock.nowEpochMs,
    });
  }, [
    clock.status.synchronized,
    countdownEnabled,
    hasStarted,
    settings.startAtJst,
  ]);

  useEffect(() => {
    if (!hasStarted) return;
    dispatchCountdown({ type: "tick", nowEpochMs: clock.nowEpochMs });
  }, [clock.nowEpochMs, hasStarted]);

  useEffect(() => {
    if (
      !hasStarted ||
      settingsOpen ||
      !countdownEnabled ||
      !settings.countdownSoundEnabled ||
      !countdownView.enabled
    ) return;

    const cue = countdownCueSequencerRef.current.next(
      countdownView.remainingSeconds,
    );
    if (cue !== null) countdownSoundPlayerRef.current.play(cue);
  }, [
    countdownEnabled,
    countdownView.enabled,
    countdownView.remainingSeconds,
    hasStarted,
    settings.countdownSoundEnabled,
    settingsOpen,
  ]);

  useEffect(() => {
    if (
      hasStarted &&
      !settingsOpen &&
      activeSlideId === "countdown" &&
      countdownState.pinArmed
    ) {
      dispatchCountdown({ type: "slide-enter" });
    }
  }, [activeSlideId, countdownState.pinArmed, hasStarted, settingsOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!hasStarted || settingsOpen) return;
      if (event.key.toLowerCase() === "s") openSettings();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasStarted, openSettings, settingsOpen]);

  useEffect(() => {
    if (!settings.requestFullscreen) return;
    if (!hasStarted || settingsOpen) return;

    window.addEventListener("pointerdown", attemptFullscreen);
    window.addEventListener("keydown", attemptFullscreen);

    return () => {
      window.removeEventListener("pointerdown", attemptFullscreen);
      window.removeEventListener("keydown", attemptFullscreen);
    };
  }, [
    attemptFullscreen,
    hasStarted,
    settings.requestFullscreen,
    settingsOpen,
  ]);

  useEffect(() => {
    if (!storageError) return;

    const retryStorage = (): void => {
      try {
        window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
        setStorageError(null);
      } catch {
        // The persistent notice remains visible until a later retry succeeds.
      }
    };
    const retryId = window.setInterval(retryStorage, 15_000);
    window.addEventListener("online", retryStorage);
    window.addEventListener("focus", retryStorage);
    window.addEventListener("pageshow", retryStorage);

    return () => {
      window.clearInterval(retryId);
      window.removeEventListener("online", retryStorage);
      window.removeEventListener("focus", retryStorage);
      window.removeEventListener("pageshow", retryStorage);
    };
  }, [settings, storageError]);

  const renderedSlides = useMemo<readonly RenderedSlide[]>(() => {
    return settings.enabledSlides.flatMap((id): readonly RenderedSlide[] => {
      const definition = SLIDE_DEFINITIONS.find((candidate) => candidate.id === id);
      const label = definition?.label ?? id;

      if (id === "program") {
        return [{ id, label, content: <ProgramSlide /> }];
      }
      if (id === "kakuzuke") {
        return [{ id, label, content: <KakuzukeSlide /> }];
      }
      if (id === "twenty-doors") {
        return [{ id, label, content: <TwentyDoorsSlide /> }];
      }
      if (id === "ogiri") {
        return [{ id, label, content: <OgiriSlide /> }];
      }
      if (id === "love-advice") {
        return [{ id, label, content: <LoveAdviceSlide /> }];
      }
      if (id === "participation") {
        if (!participationSnapshotIsFresh && !settingsOpen) return [];
        return [{
          id,
          label,
          content: !participationSnapshotIsFresh
            ? null
            : <ParticipationSlide data={participation.data} />,
        }];
      }
      if (id === "countdown") {
        return [{
          id,
          label,
          content: countdownView.enabled ? (
            <CountdownSlide view={countdownView} />
          ) : (
            <article className="content-slide countdown-placeholder">
              <strong>カウントダウンを準備しています</strong>
            </article>
          ),
        }];
      }
      const unexpectedId: never = id;
      return unexpectedId;
    });
  }, [countdownView, participation.data, participationSnapshotIsFresh, settings.enabledSlides, settingsOpen]);

  const handleCompleteSettings = (nextSettings: DisplaySettings): void => {
    const nextCountdownEnabled = nextSettings.enabledSlides.includes("countdown");
    const nextParticipationEnabled = nextSettings.enabledSlides.includes("participation");
    countdownCueSequencerRef.current.reset();
    if (nextSettings.countdownSoundEnabled && nextCountdownEnabled) {
      countdownSoundPlayerRef.current.armFromUserGesture();
    } else {
      countdownSoundPlayerRef.current.dispose();
    }

    if (nextSettings.requestFullscreen && !document.fullscreenElement) {
      attemptFullscreen();
    } else if (!nextSettings.requestFullscreen && document.fullscreenElement) {
      if (typeof document.exitFullscreen === "function") {
        void document.exitFullscreen().catch(() => undefined);
      }
    }

    try {
      window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(nextSettings));
      setStorageError(null);
    } catch {
      setStorageError(
        "設定を端末に保存できません。投影を続けながら自動で再試行します。",
      );
    }

    const nextTargetEpochMs = parseJstDateTimeInput(nextSettings.startAtJst);
    if (nextParticipationEnabled && nextCountdownEnabled) {
      void requestParticipationSourceRefresh(
        nextSettings.apiId,
        nextTargetEpochMs,
      );
    }
    dispatchCountdown({
      type: "configure",
      configuration: nextCountdownEnabled
        ? { enabled: true, targetEpochMs: nextTargetEpochMs }
        : { enabled: false, targetEpochMs: nextTargetEpochMs },
      nowEpochMs: clock.nowEpochMs,
    });
    setSettings(nextSettings);
    setSettingsApiId(nextSettings.apiId);
    setSettingsParticipationEnabled(nextParticipationEnabled);
    setHasStarted(true);
    setSettingsOpen(false);
    setProjectionRevision((revision) => revision + 1);
    setActiveSlideId(nextSettings.enabledSlides[0] ?? "program");
  };

  const fullscreenCountdown =
    hasStarted && countdownView.enabled && countdownView.displayMode === "fullscreen";
  const visibleFullscreenCountdown = fullscreenCountdown && !settingsOpen;
  const carouselPaused =
    !hasStarted || settingsOpen || !countdownView.shouldAdvanceSlides;
  const runtimeNotices = Array.from(new Set([
    storageError,
    clock.error,
    shouldLoadParticipation ? participation.error : null,
    shouldLoadParticipation ? participationRefresh.error : null,
    shouldLoadParticipation
      && participation.data !== null
      && participation.error === null
      && !participationSnapshotIsFresh
      ? participationRefresh.windowClosed
        ? "参加状況の元データが古いため、参加状況スライドを一時的に非表示にしています。更新許可期間を確認し、元データの更新後に再取得してください。"
        : participationRefreshWindowOpen
          ? "参加状況の元データ更新が遅れています。自動取得を継続しています。"
          : "参加状況の元データ更新が遅れています。カウントダウンを有効にして投影を開始するか、手動で再取得してください。"
      : null,
  ].filter((notice): notice is string => Boolean(notice))));
  const showParticipationRefreshAction = shouldLoadParticipation && Boolean(
    participation.error
      || participationRefresh.error
      || (participation.data !== null && !participationSnapshotIsFresh),
  );
  const countdownZeroElapsedMs =
    countdownView.enabled && countdownView.isStopped
      ? Math.max(
          0,
          clock.nowEpochMs - parseJstDateTimeInput(settings.startAtJst),
        )
      : 0;

  return (
    <main className={[
      "display-shell",
      visibleFullscreenCountdown ? "display-shell--fullscreen" : "",
    ].filter(Boolean).join(" ")}>
      <Slideshow
        key={projectionRevision}
        slides={renderedSlides}
        paused={carouselPaused}
        onActiveSlideChange={setActiveSlideId}
      />

      {!visibleFullscreenCountdown ? (
        <FooterBar
          announcements={settings.announcements}
          nowEpochMs={clock.nowEpochMs}
          onOpenSettings={openSettings}
        />
      ) : null}

      {visibleFullscreenCountdown && countdownView.enabled ? (
        <CountdownSlide
          view={countdownView}
          fullscreen
          zeroElapsedMs={countdownZeroElapsedMs}
          onOpenSettings={openSettings}
        />
      ) : null}

      {runtimeNotices.length > 0 && !settingsOpen && !visibleFullscreenCountdown ? (
        <button
          type="button"
          className="audience-status-indicator"
          aria-label="システム状態に注意があります。設定画面で詳細を確認"
          data-testid="audience-status-indicator"
          onClick={openSettings}
        >
          <span aria-hidden="true">!</span>
        </button>
      ) : null}

      {settingsOpen ? (
        <SettingsDialog
          key={`${settings.startAtJst}-${settings.apiId}-${settings.enabledSlides.join("-")}-${settings.announcements.join("|")}-${settings.countdownSoundEnabled}`}
          initialSettings={settings}
          canCancel={hasStarted}
          nowEpochMs={clock.nowEpochMs}
          clockStatus={clock.status}
          systemNotices={runtimeNotices}
          showParticipationRefreshAction={showParticipationRefreshAction}
          participationRefreshing={participationRefresh.refreshing}
          onApiIdChange={setSettingsApiId}
          onParticipationEnabledChange={setSettingsParticipationEnabled}
          onRefreshParticipation={(apiId, targetEpochMs) => {
            void requestParticipationSourceRefresh(apiId, targetEpochMs);
          }}
          onComplete={handleCompleteSettings}
          onCancel={cancelSettings}
        />
      ) : null}
    </main>
  );
}
